import moment from 'moment';
import { useEffect, useState } from 'react';
import { Popover, PopoverContent, PopoverTrigger } from '../../components/Popover';
import { CalendarIcon } from '../../components/svgs/icons/CalendarIcon';
import { Button } from '../Button';
import { ButtonGroup } from '../ButtonGroup';
import { Calendar } from '../Calendar';
import { Input } from '../Input';

type Props = {
	value: string;
	onChange: (value: string) => void;
};

// Common date formats to try parsing
const DATE_FORMATS = [
	'YYYY-MM-DD',
	'MM/DD/YYYY',
	'M/D/YYYY',
	'MM-DD-YYYY',
	'M-D-YYYY',
	'MM/DD/YY',
	'M/D/YY',
	'MM-DD-YY',
	'M-D-YY',
	'YYYY/MM/DD',
];

export const DateInput = ({ value, onChange }: Props) => {
	const [localValue, setLocalValue] = useState(value);
	const [isCalendarOpen, setIsCalendarOpen] = useState(false);
	const placeholder = 'YYYY-MM-DD';

	// Sync local state with prop when prop changes (e.g., after API response)
	useEffect(() => {
		setLocalValue(value);
	}, [value]);

	const displayValue = localValue && moment(localValue).isValid() ? localValue : localValue || '';

	const handleCalendarSelect = (newValue: string) => {
		setLocalValue(newValue); // Update immediately for responsiveness
		onChange(newValue); // Trigger API call immediately for calendar selection
		setIsCalendarOpen(false); // Close the calendar after selection
	};

	const handleInputChange = (newValue: string) => {
		setLocalValue(newValue); // Update display immediately, but don't trigger API yet
	};

	const handleInputBlur = () => {
		// Only trigger onChange when user is done typing and value has changed
		if (localValue === value) {
			return;
		}

		// If empty, pass through as-is
		if (!localValue.trim()) {
			onChange(localValue);
			return;
		}

		// Try to parse with various formats and normalize to YYYY-MM-DD
		const parsed = moment(localValue, DATE_FORMATS, true);
		if (parsed.isValid()) {
			const normalized = parsed.format('YYYY-MM-DD');
			setLocalValue(normalized); // Update display to normalized format
			onChange(normalized);
		} else {
			// Pass through as-is and let the handler decide what to do
			onChange(localValue);
		}
	};

	return (
		<ButtonGroup>
			<Popover open={isCalendarOpen} onOpenChange={setIsCalendarOpen}>
				<PopoverTrigger asChild>
					<Button variant='secondary'>
						<CalendarIcon />
					</Button>
				</PopoverTrigger>
				<PopoverContent side='bottom'>
					<Calendar
						mode='single'
						selected={localValue && moment(localValue).isValid() ? moment(localValue).toDate() : undefined}
						defaultMonth={localValue && moment(localValue).isValid() ? moment(localValue).toDate() : new Date()}
						onSelect={day => {
							handleCalendarSelect(moment(day).format('YYYY-MM-DD'));
						}}
					/>
				</PopoverContent>
			</Popover>
			<Input
				value={displayValue}
				onChange={e => handleInputChange(e.target.value)}
				onBlur={handleInputBlur}
				placeholder={placeholder}
			/>
		</ButtonGroup>
	);
};
